import type {Component, ComponentDefinition, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {getComponentStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {assertNever} from '@Utils/assert-never.util';
import {escapeHTML, getUniqueString} from '@Utils/string.util';
import type {CountdownConfig, CountdownDeadline, CountdownDigits, CountdownPlacement, CountdownSeparator, CountdownWallClock} from './email-maker-countdown-library.types';
import {
  COUNTDOWN_ATTRIBUTE_OFF,
  COUNTDOWN_ATTRIBUTE_ON,
  COUNTDOWN_ATTRIBUTES,
  COUNTDOWN_DEFAULT_DEADLINE_DAYS_AHEAD,
  COUNTDOWN_DEFAULT_LABEL_COLOR,
  COUNTDOWN_DEFAULT_LABEL_SIZE,
  COUNTDOWN_DIGIT_STYLE,
  COUNTDOWN_FILLER_FULL_WIDTH,
  COUNTDOWN_FILLER_HALF_WIDTH,
  COUNTDOWN_HOURS_PER_DAY,
  COUNTDOWN_LABEL_GAP,
  COUNTDOWN_LABEL_LINE_HEIGHT,
  COUNTDOWN_MILLISECONDS_PER_SECOND,
  COUNTDOWN_MINUTES_PER_HOUR,
  COUNTDOWN_NUMBER_LINE_HEIGHT,
  COUNTDOWN_PLACEMENT,
  COUNTDOWN_SECONDS_PER_MINUTE,
  COUNTDOWN_SEPARATOR,
  COUNTDOWN_SEPARATOR_CELL_PADDING,
  COUNTDOWN_SEPARATOR_GLYPHS,
  COUNTDOWN_STYLE_PROPS,
  COUNTDOWN_UNIT_CELL_PADDING,
  COUNTDOWN_UNIT_DEFAULT_LABEL_KEYS,
  COUNTDOWN_UNIT_LABEL_ATTRIBUTES,
  COUNTDOWN_UNIT_ORDER,
  COUNTDOWN_WIDTH_FIT_CONTENT,
  CountdownUnit,
} from './email-maker-countdown-library.types';

const COUNTDOWN_PART_FLAGS = {
  highlightable: false,
  hoverable: false,
  selectable: false,
  badgable: false,
  droppable: false,
  draggable: false,
  copyable: false,
  editable: false,
} as const;

export function isCountdownComponent(component: Component | undefined): component is Component {
  return component?.get('type') === EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN;
}

export function refreshCountdown(editor: Editor, component: Component): void {
  editor.getModel().skip(() => {
    component.components(getCountdownRowDefinition(readCountdownConfig(component)));
  });
}

export function refreshSelectedCountdown(editor: Editor): void {
  const selected = editor.getSelected();
  if (isCountdownComponent(selected)) {
    refreshCountdown(editor, selected);
  }
}

export function setCountdownAttribute(editor: Editor, component: Component, attribute: string, value: string): void {
  component.addAttributes({[attribute]: value});
  refreshCountdown(editor, component);
}

export function setCountdownFlag(editor: Editor, component: Component, attribute: string, isOn: boolean): void {
  setCountdownAttribute(editor, component, attribute, isOn ? COUNTDOWN_ATTRIBUTE_ON : COUNTDOWN_ATTRIBUTE_OFF);
}

export function readCountdownFlag(component: Component, attribute: string, defaultValue: boolean): boolean {
  return parseCountdownFlag(readAttribute(component, attribute), defaultValue);
}

export function parseCountdownFlag(rawValue: string, defaultValue: boolean): boolean {
  if (rawValue === '') {
    return defaultValue;
  }
  return rawValue === COUNTDOWN_ATTRIBUTE_ON;
}

export function readCountdownConfig(component: Component): CountdownConfig {
  const {showDays, unitValues} = readCountdownDigits(component);

  return {
    showDays,
    showLabels: readCountdownFlag(component, COUNTDOWN_ATTRIBUTES.SHOW_LABELS, true),
    labels: {
      [CountdownUnit.DAYS]: readCountdownLabel(component, CountdownUnit.DAYS),
      [CountdownUnit.HOURS]: readCountdownLabel(component, CountdownUnit.HOURS),
      [CountdownUnit.MINUTES]: readCountdownLabel(component, CountdownUnit.MINUTES),
      [CountdownUnit.SECONDS]: readCountdownLabel(component, CountdownUnit.SECONDS),
    },
    separator: readSeparator(component),
    placement: readPlacement(component),
    hasFixedWidth: hasCountdownFixedWidth(component),
    unitValues,
    labelSize: getComponentStyleProperty(component, COUNTDOWN_STYLE_PROPS.LABEL_SIZE) || COUNTDOWN_DEFAULT_LABEL_SIZE,
    labelColor: getComponentStyleProperty(component, COUNTDOWN_STYLE_PROPS.LABEL_COLOR) || COUNTDOWN_DEFAULT_LABEL_COLOR,
    labelFont: getComponentStyleProperty(component, COUNTDOWN_STYLE_PROPS.LABEL_FONT),
  };
}

export function readCountdownLabel(component: Component, unit: CountdownUnit): string {
  return resolveCountdownLabel(readAttribute(component, COUNTDOWN_UNIT_LABEL_ATTRIBUTES[unit]), unit);
}

export function resolveCountdownLabel(customLabel: string, unit: CountdownUnit): string {
  return customLabel !== '' ? customLabel : window.i18next.t(COUNTDOWN_UNIT_DEFAULT_LABEL_KEYS[unit]);
}

export function setCountdownDefaults(component: Component): void {
  if (readAttribute(component, COUNTDOWN_ATTRIBUTES.BLOCK_ID) === '') {
    component.addAttributes({[COUNTDOWN_ATTRIBUTES.BLOCK_ID]: getUniqueString()});
  }
  if (readAttribute(component, COUNTDOWN_ATTRIBUTES.TARGET_UTC) === '') {
    component.addAttributes(getDeadlineAttributes(buildDeadline(getDefaultWallClock(), getBrowserTimezoneCode())));
  }
}

export function readCountdownDeadline(component: Component): CountdownDeadline {
  return buildDeadlineFromAttributes(readAttribute(component, COUNTDOWN_ATTRIBUTES.TARGET_UTC), readAttribute(component, COUNTDOWN_ATTRIBUTES.TIMEZONE));
}

export function buildDeadlineFromAttributes(targetUtcRaw: string, timezoneCode: string): CountdownDeadline {
  const targetUtcSeconds = parseInt(targetUtcRaw, 10);
  const resolvedTimezoneCode = timezoneCode || getBrowserTimezoneCode();
  if (Number.isNaN(targetUtcSeconds)) {
    return buildDeadline(getDefaultWallClock(), resolvedTimezoneCode);
  }
  return {timezoneCode: resolvedTimezoneCode, targetUtcSeconds};
}

export function setCountdownDeadline(editor: Editor, component: Component, wallClock: CountdownWallClock, timezoneCode: string): void {
  component.addAttributes(getDeadlineAttributes(buildDeadline(wallClock, timezoneCode)));
  refreshCountdown(editor, component);
}

export function buildDeadline(wallClock: CountdownWallClock, timezoneCode: string): CountdownDeadline {
  return {
    timezoneCode,
    targetUtcSeconds: Math.floor(wallClockToUtcMilliseconds(wallClock, timezoneCode) / COUNTDOWN_MILLISECONDS_PER_SECOND),
  };
}

export function getDeadlineWallClock(deadline: CountdownDeadline): CountdownWallClock {
  const {second: _second, ...wallClock} = getZonedTimeParts(deadline.targetUtcSeconds * COUNTDOWN_MILLISECONDS_PER_SECOND, deadline.timezoneCode);
  return wallClock;
}

export function dateToWallClock(date: Date): CountdownWallClock {
  return {year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: date.getHours(), minute: date.getMinutes()};
}

export function wallClockToDate(wallClock: CountdownWallClock): Date {
  return new Date(wallClock.year, wallClock.month - 1, wallClock.day, wallClock.hour, wallClock.minute, 0, 0);
}

export function findCountdownComponents(editor: Editor): Component[] {
  return editor.getWrapper()?.findType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN) ?? [];
}

export function updateCountdownDigitsInDom(component: Component): void {
  const el = component.getEl();
  if (!el) {
    return;
  }
  const {unitValues} = readCountdownDigits(component);
  el.querySelectorAll<HTMLElement>(`[${COUNTDOWN_ATTRIBUTES.UNIT}]`).forEach((digitsEl) => {
    const unit = Object.values(CountdownUnit).find((countdownUnit) => countdownUnit === digitsEl.getAttribute(COUNTDOWN_ATTRIBUTES.UNIT));
    if (unit !== undefined && digitsEl.textContent !== unitValues[unit]) {
      digitsEl.textContent = unitValues[unit];
    }
  });
}

function readCountdownDigits(component: Component): CountdownDigits {
  const showDays = readCountdownFlag(component, COUNTDOWN_ATTRIBUTES.SHOW_DAYS, true);
  return {showDays, unitValues: getCountdownUnitValues(getSecondsRemaining(readCountdownDeadline(component)), showDays)};
}

export function getSecondsRemaining(deadline: CountdownDeadline): number {
  return deadline.targetUtcSeconds - Math.floor(Date.now() / COUNTDOWN_MILLISECONDS_PER_SECOND);
}

export function getCountdownUnitValues(secondsRemaining: number, showDays: boolean): Record<CountdownUnit, string> {
  const secondsPerHour = COUNTDOWN_MINUTES_PER_HOUR * COUNTDOWN_SECONDS_PER_MINUTE;
  const secondsPerDay = COUNTDOWN_HOURS_PER_DAY * secondsPerHour;
  const remaining = Math.max(0, secondsRemaining);
  const days = Math.floor(remaining / secondsPerDay);
  const hours = showDays ? Math.floor((remaining % secondsPerDay) / secondsPerHour) : Math.floor(remaining / secondsPerHour);
  const minutes = Math.floor((remaining % secondsPerHour) / COUNTDOWN_SECONDS_PER_MINUTE);
  const seconds = remaining % COUNTDOWN_SECONDS_PER_MINUTE;

  return {
    [CountdownUnit.DAYS]: formatCountdownDigits(days),
    [CountdownUnit.HOURS]: formatCountdownDigits(hours),
    [CountdownUnit.MINUTES]: formatCountdownDigits(minutes),
    [CountdownUnit.SECONDS]: formatCountdownDigits(seconds),
  };
}

function formatCountdownDigits(value: number): string {
  return String(value).padStart(2, '0');
}

export function assignFreshCountdownId(component: Component): void {
  component.addAttributes({[COUNTDOWN_ATTRIBUTES.BLOCK_ID]: getUniqueString()});
}

export function getCountdownRowDefinition(config: CountdownConfig): ComponentDefinition {
  const units = config.showDays ? COUNTDOWN_UNIT_ORDER : COUNTDOWN_UNIT_ORDER.filter((unit) => unit !== CountdownUnit.DAYS);
  const separatorGlyph = COUNTDOWN_SEPARATOR_GLYPHS[config.separator];

  return {
    type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
    tagName: 'tr',
    ...COUNTDOWN_PART_FLAGS,
    components: withPlacementFillers(
      units.flatMap((unit, index) => {
        const unitCell = getCountdownUnitDefinition(unit, config);
        if (index === 0 || separatorGlyph === '') {
          return [unitCell];
        }
        return [getCountdownSeparatorDefinition(separatorGlyph), unitCell];
      }),
      config.hasFixedWidth ? config.placement : COUNTDOWN_PLACEMENT.SPREAD
    ),
  };
}

function withPlacementFillers(cells: ComponentDefinition[], placement: CountdownPlacement): ComponentDefinition[] {
  switch (placement) {
    case COUNTDOWN_PLACEMENT.LEFT:
      return [...cells, getFillerCellDefinition(COUNTDOWN_FILLER_FULL_WIDTH)];
    case COUNTDOWN_PLACEMENT.RIGHT:
      return [getFillerCellDefinition(COUNTDOWN_FILLER_FULL_WIDTH), ...cells];
    case COUNTDOWN_PLACEMENT.CENTER:
      return [getFillerCellDefinition(COUNTDOWN_FILLER_HALF_WIDTH), ...cells, getFillerCellDefinition(COUNTDOWN_FILLER_HALF_WIDTH)];
    case COUNTDOWN_PLACEMENT.SPREAD:
      return cells;
    default:
      return assertNever(placement);
  }
}

function getFillerCellDefinition(width: string): ComponentDefinition {
  return {
    type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
    tagName: 'td',
    ...COUNTDOWN_PART_FLAGS,
    style: {width, padding: '0'},
    components: [],
  };
}

export function hasCountdownFixedWidth(component: Component): boolean {
  const width = getComponentStyleProperty(component, 'width');
  return width !== '' && width !== 'auto' && width !== COUNTDOWN_WIDTH_FIT_CONTENT;
}

function getCountdownSeparatorDefinition(glyph: string): ComponentDefinition {
  return {
    type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
    tagName: 'td',
    ...COUNTDOWN_PART_FLAGS,
    style: {'text-align': 'center', 'vertical-align': 'top', padding: COUNTDOWN_SEPARATOR_CELL_PADDING},
    components: [getCountdownTextDefinition(glyph, {'line-height': COUNTDOWN_NUMBER_LINE_HEIGHT})],
  };
}

function getCountdownUnitDefinition(unit: CountdownUnit, config: CountdownConfig): ComponentDefinition {
  const digits: ComponentDefinition = {...getCountdownTextDefinition(config.unitValues[unit], COUNTDOWN_DIGIT_STYLE), attributes: {[COUNTDOWN_ATTRIBUTES.UNIT]: unit}};
  const parts = [digits];
  if (config.showLabels) {
    parts.push(getCountdownTextDefinition(config.labels[unit], getLabelStyle(config)));
  }
  return {
    type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL,
    tagName: 'td',
    ...COUNTDOWN_PART_FLAGS,
    style: {'text-align': 'center', 'vertical-align': 'top', padding: COUNTDOWN_UNIT_CELL_PADDING},
    components: parts,
  };
}

function getLabelStyle(config: CountdownConfig): Record<string, string> {
  const style: Record<string, string> = {
    'font-size': config.labelSize,
    'font-weight': 'normal',
    'line-height': COUNTDOWN_LABEL_LINE_HEIGHT,
    'padding-top': COUNTDOWN_LABEL_GAP,
    color: config.labelColor,
  };
  if (config.labelFont !== '') {
    style['font-family'] = config.labelFont;
  }
  return style;
}

function getCountdownTextDefinition(text: string, style: Record<string, string>): ComponentDefinition {
  return {
    type: EMAIL_MAKER_COMPONENT_TYPES.default.TEXT,
    tagName: 'div',
    ...COUNTDOWN_PART_FLAGS,
    style: {margin: '0', ...style},
    components: escapeHTML(text),
  };
}

function readSeparator(component: Component): CountdownSeparator {
  const raw = getComponentStyleProperty(component, COUNTDOWN_STYLE_PROPS.SEPARATOR);
  const known = Object.values(COUNTDOWN_SEPARATOR).find((value) => value === raw);
  return known ?? COUNTDOWN_SEPARATOR.COLON;
}

function readPlacement(component: Component): CountdownPlacement {
  const raw = getComponentStyleProperty(component, COUNTDOWN_STYLE_PROPS.PLACEMENT);
  const known = Object.values(COUNTDOWN_PLACEMENT).find((value) => value === raw);
  return known ?? COUNTDOWN_PLACEMENT.SPREAD;
}

function readAttribute(component: Component, attribute: string): string {
  const attributes = component.getAttributes() as Record<string, unknown>;
  const value = attributes[attribute];
  return typeof value === 'string' ? value : '';
}

function getDeadlineAttributes(deadline: CountdownDeadline): Record<string, string> {
  return {
    [COUNTDOWN_ATTRIBUTES.TIMEZONE]: deadline.timezoneCode,
    [COUNTDOWN_ATTRIBUTES.TARGET_UTC]: String(deadline.targetUtcSeconds),
  };
}

function getDefaultWallClock(): CountdownWallClock {
  const date = new Date();
  date.setDate(date.getDate() + COUNTDOWN_DEFAULT_DEADLINE_DAYS_AHEAD);
  date.setMinutes(0, 0, 0);
  return dateToWallClock(date);
}

function getBrowserTimezoneCode(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Converts a wall-clock time in a zone to UTC. Two passes: the zone's offset at the first guess can differ from
 * the offset at the true instant around a DST switch, so the second pass corrects it.
 */
function wallClockToUtcMilliseconds(wallClock: CountdownWallClock, timezoneCode: string): number {
  const digitsReadAsUtc = Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day, wallClock.hour, wallClock.minute);
  const firstGuess = digitsReadAsUtc - getTimezoneOffsetMilliseconds(digitsReadAsUtc, timezoneCode);
  return digitsReadAsUtc - getTimezoneOffsetMilliseconds(firstGuess, timezoneCode);
}

/** How far the given time zone is from UTC at that moment, in milliseconds. */
function getTimezoneOffsetMilliseconds(utcMilliseconds: number, timezoneCode: string): number {
  const zoned = getZonedTimeParts(utcMilliseconds, timezoneCode);
  const zonedAsUtc = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
  return zonedAsUtc - utcMilliseconds;
}

/** The calendar digits a clock in the given time zone shows at that UTC moment. */
function getZonedTimeParts(utcMilliseconds: number, timezoneCode: string): CountdownWallClock & {second: number} {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezoneCode,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMilliseconds));

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((candidate) => candidate.type === type);
    return part ? parseInt(part.value, 10) : 0;
  };

  return {year: read('year'), month: read('month'), day: read('day'), hour: read('hour'), minute: read('minute'), second: read('second')};
}
